import { pick } from 'lodash';
import {
  prepareMarkItemAttributes,
  prepareMarkItemRelationships
} from 'services/assist-api/entities/test-instance-response-mark-item/hooks.helpers';
import type { PostTestInstanceResponseMarkItem } from 'services/assist-api/entities/test-instance-response-mark-item/types';
import type { PostAndPatchMarkFields, PostTestInstanceResponseMark } from './types';

function prepareIncludedMarkItems(
  testInstanceResponseMarkItems: PostTestInstanceResponseMarkItem[]
): MutationBodyIncluded {
  return testInstanceResponseMarkItems.map((markItem, index: number) => ({
    lid: `${index + 1}`.toString(),
    type: 'TestInstanceResponseMarkItem',
    attributes: prepareMarkItemAttributes(markItem),
    relationships: prepareMarkItemRelationships(markItem)
  }));
}

function prepareCommonRelationships(
  testInstanceResponseMark: Partial<PostAndPatchMarkFields>
): MutationBodyRelationships {
  return {
    ...(testInstanceResponseMark.activeMarkerId
      ? {
          activeMarker: {
            data: {
              id: `/api/v2/test-instance-response-markers/${testInstanceResponseMark.activeMarkerId}`,
              type: 'TestInstanceResponseMarker'
            }
          }
        }
      : {}),
    ...(testInstanceResponseMark.testInstanceResponseId
      ? {
          testInstanceResponse: {
            data: {
              id: `/api/v2/test-instance-responses/${testInstanceResponseMark.testInstanceResponseId}`,
              type: 'TestInstanceResponse'
            }
          }
        }
      : {}),
    ...(testInstanceResponseMark.testRubricId
      ? {
          testRubric: {
            data: {
              id: `/api/v2/test-rubrics/${testInstanceResponseMark.testRubricId}`,
              type: 'TestRubric'
            }
          }
        }
      : {}),
    ...(testInstanceResponseMark.tagIds
      ? {
          tags: {
            data: testInstanceResponseMark.tagIds.map((tagId) => ({
              id: `/api/v2/tags/${tagId}`,
              type: 'Tag'
            }))
          }
        }
      : {}),
    ...(testInstanceResponseMark.activeReviewerId
      ? {
          activeReviewer: {
            data: {
              id: `/api/v2/customers-users/${testInstanceResponseMark.activeReviewerId}`,
              type: 'CustomerUsers'
            }
          }
        }
      : {})
  };
}

function preparePostRelationships(
  testInstanceResponseMark: PostTestInstanceResponseMark
): MutationBodyRelationships {
  return {
    ...prepareCommonRelationships(testInstanceResponseMark),
    ...(testInstanceResponseMark.testInstanceResponseMarkItems
      ? {
          markItems: {
            data: testInstanceResponseMark.testInstanceResponseMarkItems.map((_, index) => ({
              lid: `${index + 1}`.toString(),
              type: 'TestInstanceResponseMarkItem'
            }))
          }
        }
      : {})
  };
}

function prepareCommonAttributes(
  testInstanceResponseMarkFields: Partial<PostAndPatchMarkFields>
): MutationBodyAttributes {
  return {
    ...pick(testInstanceResponseMarkFields, ['status', 'needsAttention', 'comment'])
  };
}

export function preparePatchTestInstanceResponseMark(
  testInstanceResponseMark: Partial<PostAndPatchMarkFields>
): PostMutationBody {
  return {
    data: {
      type: 'TestInstanceResponseMark',
      attributes: prepareCommonAttributes(testInstanceResponseMark),
      relationships: prepareCommonRelationships(testInstanceResponseMark)
    }
  };
}

export function preparePostTestInstanceResponseMark(
  testInstanceResponseMark: PostTestInstanceResponseMark
): PostMutationBody {
  return {
    data: {
      type: 'TestInstanceResponseMark',
      attributes: prepareCommonAttributes(testInstanceResponseMark),
      relationships: preparePostRelationships(testInstanceResponseMark)
    },
    included: prepareIncludedMarkItems(testInstanceResponseMark.testInstanceResponseMarkItems)
  };
}
